import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Loader2, Sparkles, RotateCcw } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Modulo82ReinicioRapido() {
    const [accionReinicio, setAccionReinicio] = useState('');
    const [limiteTiempo, setLimiteTiempo] = useState('');
    const [regla, setRegla] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');
    const [nivelEjecucion, setNivelEjecucion] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/reinicio_rapido`, { withCredentials: true });
                if (data.data) {
                    if (data.data.accionReinicio) setAccionReinicio(data.data.accionReinicio);
                    if (data.data.limiteTiempo) setLimiteTiempo(data.data.limiteTiempo);
                    if (data.data.regla) setRegla(data.data.regla);
                }
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch {} finally { setLoadingDraft(false); }
        })();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try { await axios.post(`${API_URL}/api/user/draft/save`, { module_id: 'reinicio_rapido', data: { accionReinicio, limiteTiempo, regla } }, { withCredentials: true }); } catch {} finally { setSavingDraft(false); }
    }, [accionReinicio, limiteTiempo, regla]);

    useEffect(() => { if (loadingDraft || readOnly) return; const t = setTimeout(saveDraft, 2000); return () => clearTimeout(t); }, [accionReinicio, limiteTiempo, regla, saveDraft, loadingDraft, readOnly]);

    const canSubmit = !readOnly && (accionReinicio.trim().length > 0 && limiteTiempo.trim().length > 0 && regla.trim().length > 0);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/user/modules/reinicio-rapido/submit`, {
                accion_reinicio: accionReinicio, limite_tiempo: limiteTiempo, regla
            }, { withCredentials: true });
            setAiInsight(data.ai_insight);
            setNivelEjecucion(data.nivel_ejecucion);
            setSubmitted(true);
            toast.success('Reinicio rápido completado');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar'); }
        finally { setLoading(false); }
    };

    if (loadingDraft) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (submitted) return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <div className="flex-1 flex items-center justify-center p-4">
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl"><CardContent className="pt-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#7A9BA8] to-[#5F8494] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"><RotateCcw className="h-10 w-10 text-white" /></div>
                    <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2" data-testid="reinicio-rapido-success-title">Protocolo de reinicio definido</h2>
                    <p className="text-[#7A9BA8] font-medium mb-4">Ahora puedes reactivarte sin depender de la motivación</p>
                    {nivelEjecucion && <div className="inline-block px-4 py-1.5 bg-[#5FAF9D]/10 text-[#5FAF9D] rounded-full text-sm font-medium mb-4">Ejecución: {nivelEjecucion}</div>}
                    {aiInsight && <div className="bg-[#F5F7F8] border-l-4 border-[#7A9BA8] p-4 rounded-r-xl text-left mb-6"><div className="flex items-start gap-3"><Sparkles className="h-5 w-5 text-[#7A9BA8] shrink-0 mt-0.5" /><p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p></div></div>}
                    <div className="mb-6"><ModuleFeedback moduleId="reinicio_rapido" chapter={8} /></div>
                    <Link to="/usuario"><Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl" data-testid="continue-after-reinicio-rapido">Continuar</Button></Link>
                </CardContent></Card>
            </div><SENDAFooter /></div>
    );

    return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <header className="bg-white/80 backdrop-blur-xl border-b border-[#EAE8E3]/50 sticky top-0 z-50">
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
                    <Link to="/usuario" className="flex items-center gap-2 text-[#3E5F6E] hover:text-[#2F4A57]"><ArrowLeft className="h-5 w-5" /><span className="text-sm font-medium">Volver</span></Link>
                    <span className="text-xs text-[#9AA5B1]">{savingDraft ? 'Guardando...' : 'Borrador guardado'}</span>
                </div>
            </header>
            <main className="flex-1 max-w-3xl mx-auto px-4 py-8 w-full">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2"><div className="w-10 h-10 bg-[#3E5F6E] rounded-xl flex items-center justify-center"><RotateCcw className="h-5 w-5 text-white" /></div><span className="text-xs font-semibold text-[#9AA5B1] uppercase tracking-wider">Capítulo 8 · Módulo 8.2</span></div>
                    <h1 className="text-2xl sm:text-3xl font-light text-[#1F2A30] font-['Outfit'] mb-2">Reinicio rápido</h1>
                    <p className="text-[#616E7C]">Define un protocolo de reinicio inmediato para cuando tu sistema se interrumpa. Diseñado para momentos de baja energía.</p>
                </div>
                <div className="space-y-6">
                    <div>
                        <Label className="text-sm font-semibold text-[#1F2A30] mb-2 block">Acción de reinicio</Label>
                        <p className="text-xs text-[#9AA5B1] mb-2">¿Cuál es la acción más simple que puedes hacer para reactivar tu sistema?</p>
                        <Textarea value={accionReinicio} onChange={e => setAccionReinicio(e.target.value)} placeholder="Una acción mínima que te reactive..." className="min-h-[80px] border-[#DCE3E8] focus:border-[#3E5F6E]" data-testid="reinicio-accion" />
                    </div>
                    <div>
                        <Label className="text-sm font-semibold text-[#1F2A30] mb-2 block">Límite de tiempo</Label>
                        <p className="text-xs text-[#9AA5B1] mb-2">¿En cuánto tiempo debe ejecutarse? (ej: 2 minutos, 5 minutos)</p>
                        <Textarea value={limiteTiempo} onChange={e => setLimiteTiempo(e.target.value)} placeholder="Ej: 2 minutos" className="min-h-[50px] border-[#DCE3E8] focus:border-[#3E5F6E]" data-testid="reinicio-limite-tiempo" />
                    </div>
                    <div>
                        <Label className="text-sm font-semibold text-[#1F2A30] mb-2 block">Regla de reinicio</Label>
                        <p className="text-xs text-[#9AA5B1] mb-2">Define una regla clara: SI [situación], ENTONCES [acción de reinicio]</p>
                        <Textarea value={regla} onChange={e => setRegla(e.target.value)} placeholder="SI me detengo por más de un día, ENTONCES..." className="min-h-[80px] border-[#DCE3E8] focus:border-[#3E5F6E]" data-testid="reinicio-regla" />
                    </div>
                    <Button onClick={handleSubmit} disabled={!canSubmit || loading} className="w-full bg-[#3E5F6E] hover:bg-[#2F4A57] text-white py-3 rounded-xl" data-testid="submit-reinicio-rapido">
                        {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Analizando...</> : 'Completar reinicio rápido'}
                    </Button>
                </div>
            </main>
            <SENDAFooter />
        </div>
    );
}
